import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardIcon from '@mui/icons-material/Keyboard';

interface Shortcut {
  keys: string[];
  label: string;
}

interface ShortcutGroup {
  title: string;
  items: Shortcut[];
}

const GROUPS: ShortcutGroup[] = [
  {
    title: 'ניווט כללי',
    items: [
      { keys: ['⌘', 'K'], label: 'פתח חיפוש מהיר (חברות, דפים, פעולות)' },
      { keys: ['?'], label: 'הצג את הקיצורים האלה' },
      { keys: ['ESC'], label: 'סגור חלון / חיפוש מהיר / דרור' },
    ],
  },
  {
    title: 'מסך נוכחי',
    items: [
      { keys: ['⌘', 'P'], label: 'הדפס את הדף (PDF דרך הדפדפן)' },
      { keys: ['⌘', 'F'], label: 'חיפוש בתוך הדף (תכונה של הדפדפן)' },
    ],
  },
  {
    title: 'תפריט הקשרי (חוצה)',
    items: [
      { keys: ['לחיצה ימנית'], label: 'תפריט פעולות על שורה (סמן, פתח משימה, AI)' },
      { keys: ['⌘', 'Enter'], label: 'שלח הערה / רישום במצבי טופס' },
    ],
  },
  {
    title: 'ביקורת חוצה — מקלדת',
    items: [
      { keys: ['J'], label: 'הבא — שורה למטה' },
      { keys: ['K'], label: 'הקודם — שורה למעלה' },
      { keys: ['D'], label: 'סמן כתקין (עם הסיבה האחרונה שבחרת)' },
      { keys: ['T'], label: 'פתח משימה במשרד' },
      { keys: ['E'], label: 'הסבר AI על החודש האחרון' },
      { keys: ['W'], label: 'הוסף / הסר מהמועדפים' },
      { keys: ['S'], label: 'נחבא ל-7 ימים' },
      { keys: ['רווח'], label: 'הוסף הערה' },
    ],
  },
  {
    title: 'דגימה ובחירה',
    items: [
      { keys: ['↑ / ↓'], label: 'בחר פריט בחיפוש המהיר' },
      { keys: ['Enter'], label: 'בחר / הפעל / שלח' },
    ],
  },
  {
    title: 'קפיצה לדפים אחרונים',
    items: [
      { keys: ['Alt', '1'], label: 'קפוץ לדף שביקרת בו לאחרונה ביותר' },
      { keys: ['Alt', '2..9'], label: 'הדפים הקודמים בהיסטוריה — קצר ב-Alt + מספר' },
    ],
  },
];

/** Press '?' anywhere to open a cheat-sheet of every keyboard
 * shortcut in Maven. Same UX as office. The '?' listener is global
 * but ignores typing inside inputs / textareas. */
export default function ShortcutsOverlay() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === '?' && !e.metaKey && !e.ctrlKey) {
        const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || (e.target as HTMLElement | null)?.isContentEditable) {
          return;
        }
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 3, py: 2,
        borderBottom: 1, borderColor: 'divider',
      }}>
        <KeyboardIcon sx={{ color: 'primary.main' }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography sx={{ fontWeight: 700 }}>קיצורי מקשים</Typography>
          <Typography variant="caption" color="text.secondary">
            הקש <kbd>?</kbd> בכל מקום כדי לפתוח את הרשימה הזו
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => setOpen(false)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 3,
        }}>
          {GROUPS.map((g) => (
            <Box key={g.title}>
              <Typography sx={{
                fontSize: 11, fontWeight: 700,
                color: 'text.secondary',
                textTransform: 'uppercase' as const,
                letterSpacing: '0.06em',
                mb: 1.5,
              }}>
                {g.title}
              </Typography>
              <Stack spacing={1}>
                {g.items.map((s) => (
                  <Box
                    key={s.label}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Typography sx={{ fontSize: 14, flexGrow: 1 }}>{s.label}</Typography>
                    <Stack direction="row" spacing={0.5}>
                      {s.keys.map((k, i) => (
                        <Box
                          key={i}
                          component="kbd"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: 12,
                            fontWeight: 700,
                            px: 1, py: 0.25,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.default',
                            color: 'text.primary',
                            minWidth: 28,
                            textAlign: 'center',
                          }}
                        >
                          {k}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
